import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { api } from '../services/api';
import { getLocalDateInputValue } from '../utils/dateUtils';

const getInitialFormState = () => ({
  medicationId: null,
  name: '',
  category: '',
  laboratory: '',
  lot: '',
  expiry: '',
  quantity: '',
  unit: 'caixas',
  min: '',
  entryDate: getLocalDateInputValue(),
});

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('pt-BR').format(date);
};

const isExpiringSoon = (value) => {
  if (!value) return false;
  const expiryDate = new Date(value);
  if (Number.isNaN(expiryDate.getTime())) return false;
  const diffInDays = (expiryDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24);
  return diffInDays <= 60 && diffInDays >= 0;
};

const getStockStatus = (item) => {
  const currentQuantity = Number(item.totalQuantity || 0);
  if (!item.min) {
    return { label: 'Em estoque', tone: 'ok' };
  }
  if (currentQuantity <= item.min) {
    return { label: 'Crítico', tone: 'danger' };
  }
  if (currentQuantity <= item.min * 1.3) {
    return { label: 'Atenção', tone: 'warning' };
  }
  return { label: 'Em estoque', tone: 'ok' };
};

const daysUntilExpiry = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return Math.round((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
};

export default function Estoque() {
  const { currentUser } = useOutletContext();
  const navigate = useNavigate();
  const [medications, setMedications] = useState([]);
  const [loadingInventory, setLoadingInventory] = useState(true);
  const [stockQuery, setStockQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('entry');
  const [editingMedicationId, setEditingMedicationId] = useState(null);
  const [formData, setFormData] = useState(getInitialFormState);
  const [isMedicationFocused, setIsMedicationFocused] = useState(false);
  const [sortConfig, setSortConfig] = useState({ field: 'name', direction: 'asc' });
  const suggestionTimeout = useRef(null);

  const medicationSuggestions = useMemo(() => {
    const query = formData.name.trim().toLowerCase();
    if (!query) return [];
    return medications.filter((med) => med.name.toLowerCase().includes(query)).slice(0, 6);
  }, [formData.name, medications]);

  useEffect(() => {
    fetchInventory();
    return () => {
      if (suggestionTimeout.current) {
        clearTimeout(suggestionTimeout.current);
      }
    };
  }, []);

  const fetchInventory = async () => {
    try {
      setLoadingInventory(true);
      const { data } = await api.get('/inventory/medications');
      setMedications(data);
    } catch (error) {
      console.error('Erro ao carregar estoque', error);
    } finally {
      setLoadingInventory(false);
    }
  };

  const stats = useMemo(() => {
    const lowStock = medications.filter((item) => item.min && Number(item.totalQuantity || 0) <= item.min).length;
    const expiringSoon = medications.filter((item) => isExpiringSoon(item.lastExpiry)).length;
    const totalUnits = medications.reduce((acc, item) => acc + Number(item.totalQuantity || 0), 0);
    return {
      lowStock,
      expiringSoon,
      totalUnits,
      items: medications.length,
    };
  }, [medications]);

  const filteredStockItems = useMemo(() => {
    const q = stockQuery.trim().toLowerCase();
    if (!q) return medications;
    return medications.filter((item) =>
      [item.name, item.laboratory, item.category, item.unit, item.lastLot, item.lastExpiry, item.lastEntryDate]
        .map((value) => String(value ?? '').toLowerCase())
        .some((value) => value.includes(q))
    );
  }, [medications, stockQuery]);

  const sortedStockItems = useMemo(() => {
    const { field, direction } = sortConfig;
    return [...filteredStockItems].sort((a, b) => {
      const getValue = (item) => {
        if (field === 'lastEntryDate' || field === 'lastExpiry') {
          return new Date(item[field] || 0).getTime();
        }
        if (field === 'totalQuantity') {
          return Number(item.totalQuantity || 0);
        }
        return (item[field] || '').toString().toLowerCase();
      };
      const aValue = getValue(a);
      const bValue = getValue(b);
      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredStockItems, sortConfig]);

  const handleSort = (field) => {
    setSortConfig((prev) => {
      if (prev.field === field) {
        return { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { field, direction: 'asc' };
    });
  };

  const resetForm = () => {
    setFormData(getInitialFormState());
    setIsMedicationFocused(false);
    if (suggestionTimeout.current) {
      clearTimeout(suggestionTimeout.current);
    }
  };

  const handleOpenEntryModal = () => {
    resetForm();
    setModalType('medication');
    setEditingMedicationId(null);
    setShowModal(true);
  };

  const handleOpenMedicationModal = (medication = null) => {
    setModalType('medication');
    setEditingMedicationId(medication?._id || null);
    setFormData({
      ...getInitialFormState(),
      medicationId: medication?._id || null,
      name: medication?.name || '',
      category: medication?.category || '',
      laboratory: medication?.laboratory || '',
      unit: medication?.unit || 'caixas',
      min: medication?.min !== undefined ? String(medication.min) : '',
    });
    setIsMedicationFocused(false);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    resetForm();
    setShowModal(false);
    setModalType('entry');
    setEditingMedicationId(null);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleMedicationFocus = () => {
    if (modalType !== 'entry') return;
    if (suggestionTimeout.current) {
      clearTimeout(suggestionTimeout.current);
    }
    setIsMedicationFocused(true);
  };

  const handleMedicationBlur = () => {
    if (modalType !== 'entry') return;
    suggestionTimeout.current = setTimeout(() => {
      setIsMedicationFocused(false);
    }, 120);
  };

  const handleSelectMedication = (medication) => {
    if (suggestionTimeout.current) {
      clearTimeout(suggestionTimeout.current);
    }
    const normalizedMin = medication.min ?? '';
    setFormData((prev) => ({
      ...prev,
      medicationId: medication._id,
      name: medication.name,
      category: medication.category || prev.category,
      laboratory: medication.laboratory || prev.laboratory,
      unit: medication.unit || prev.unit,
      min: normalizedMin !== '' ? String(normalizedMin) : prev.min,
    }));
    setIsMedicationFocused(false);
  };

  const ensureMedicationExists = async () => {
    const trimmedName = formData.name.trim();
    if (formData.medicationId) {
      return formData.medicationId;
    }

    const existing = medications.find((med) => med.name.toLowerCase() === trimmedName.toLowerCase());
    if (existing) {
      return existing._id;
    }

    const payload = {
      name: trimmedName,
      category: formData.category.trim(),
      laboratory: formData.laboratory.trim(),
      unit: formData.unit.trim() || 'unid.',
      min: Number(formData.min) || 0,
    };
    const { data } = await api.post('/inventory/medications', payload);
    return data._id;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedName = formData.name.trim();
    if (!trimmedName) {
      return;
    }

    try {
      if (modalType === 'medication') {
        const payload = {
          name: trimmedName,
          category: formData.category.trim(),
          laboratory: formData.laboratory.trim(),
          unit: formData.unit.trim() || 'unid.',
          min: Number(formData.min) || 0,
        };

        if (editingMedicationId) {
          await api.put(`/inventory/medications/${editingMedicationId}`, payload);
        } else {
          await api.post('/inventory/medications', payload);
        }
        await fetchInventory();
        handleCloseModal();
        return;
      }

      if (!formData.quantity) {
        return;
      }

      const medicationId = await ensureMedicationExists();

      await api.post('/inventory/movements', {
        medicationId,
        type: 'Entrada',
        quantity: Number(formData.quantity),
        lot: formData.lot.trim(),
        expiry: formData.expiry || null,
        patient: 'Entrada manual',
        note: '',
        date: formData.entryDate,
      });

      await fetchInventory();
      handleCloseModal();
    } catch (error) {
      console.error('Erro ao salvar dados do estoque', error);
    }
  };

  const handleNavigateToMedicationDetails = (medicationData) => {
    const id = medicationData._id || medicationData.id;
    if (id) {
      navigate(`/estoque/medicamentos/${id}`);
    }
  };

  const shouldShowSuggestions =
    modalType === 'entry' && isMedicationFocused && formData.name.trim().length > 0 && medicationSuggestions.length > 0;

  const modalTitle = editingMedicationId ? 'Editar medicamento' : 'Novo medicamento';
  const submitLabel = editingMedicationId ? 'Salvar alterações' : 'Cadastrar medicamento';

  return (
    <div className="inventory-card" style={{ paddingTop: 44, paddingBottom: 56, color: '#1C1C1E', gap: 16, alignSelf: 'flex-start', width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            background: '#FBE0E5', color: '#9F4A60',
            padding: '4px 10px', borderRadius: 999,
            fontSize: 11, letterSpacing: '-0.005em', fontWeight: 600, whiteSpace: 'nowrap',
          }}>
            <span style={{ width: 5, height: 5, borderRadius: 999, background: '#C97A8F' }} />
            Controle de estoque
          </div>
          <h1 style={{ fontWeight: 700, fontSize: 38, lineHeight: 1.18, letterSpacing: '-0.035em', margin: '10px 0 0', color: '#1C1C1E' }}>
            Estoque
          </h1>
          <div style={{ marginTop: 6, fontSize: 14, color: '#6E6E73', letterSpacing: '-0.005em' }}>
            Acompanhe entradas, validade e status dos medicamentos
          </div>
        </div>
        <button className="mac-button" onClick={handleOpenEntryModal} style={{ marginTop: 6 }}>
          + Novo medicamento
        </button>
      </div>

      {/* KPI Cards */}
      {(() => {
        const renderKpiIcon = (kind, color) => {
          const s = { width: 17, height: 17, viewBox: '0 0 24 24', fill: 'none', stroke: color, strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' };
          if (kind === 'boxes')   return <svg {...s}><rect x="3" y="3" width="8" height="8" rx="1"/><rect x="13" y="3" width="8" height="8" rx="1"/><rect x="3" y="13" width="8" height="8" rx="1"/><rect x="13" y="13" width="8" height="8" rx="1"/></svg>;
          if (kind === 'package') return <svg {...s}><path d="M21 8l-9 4-9-4 9-4z M3 8v8l9 4 9-4V8 M12 12v8"/></svg>;
          if (kind === 'alert')   return <svg {...s}><path d="M12 3l10 18H2z"/><path d="M12 10v5M12 18.5v.5"/></svg>;
          if (kind === 'clock')   return <svg {...s}><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>;
          return null;
        };
        const kpis = [
          { label: 'Tipos cadastrados',   value: stats.items,                                                     unit: '',   subtitle: 'medicamentos distintos no catálogo', accent: '#7A4A86', bg: ['#EFE3F0','#DECCDE'], icon: 'boxes'   },
          { label: 'Total em estoque',     value: stats.totalUnits,                                                unit: '',   subtitle: 'soma de todas as quantidades',       accent: '#3D8068', bg: ['#E6F1EB','#D2E5DC'], icon: 'package' },
          { label: 'Baixo estoque',        value: stats.lowStock,                                                  unit: '',   subtitle: 'abaixo da quantidade mínima',        accent: '#A66730', bg: ['#FBEEDF','#F4DCC4'], icon: 'alert'   },
          { label: 'Vencendo em 60 dias',  value: stats.expiringSoon,                                              unit: '',   subtitle: 'lotes com validade próxima',         accent: '#9F4A60', bg: ['#FBE0E5','#F4C4CE'], icon: 'clock'   },
        ];
        return (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
            {kpis.map((k) => (
              <div key={k.label} style={{ background: `linear-gradient(160deg, ${k.bg[0]} 0%, ${k.bg[1]} 100%)`, borderRadius: 22, padding: '18px 20px 20px', minHeight: 130, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.55), 0 1px 2px rgba(28,28,30,0.04), 0 8px 24px -16px rgba(28,28,30,0.18)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'rgba(28,28,30,0.65)', whiteSpace: 'nowrap' }}>{k.label}</div>
                  <div style={{ width: 30, height: 30, borderRadius: 9, background: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.7)', flexShrink: 0 }}>
                    {renderKpiIcon(k.icon, k.accent)}
                  </div>
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                    <span style={{ fontSize: 36, fontWeight: 700, lineHeight: 1, letterSpacing: '-0.045em', color: k.accent, fontVariantNumeric: 'tabular-nums' }}>
                      {k.value.toLocaleString('pt-BR')}
                    </span>
                  </div>
                  <div style={{ marginTop: 8, fontSize: 12, color: 'rgba(28,28,30,0.6)', letterSpacing: '-0.005em' }}>{k.subtitle}</div>
                </div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Search */}
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1, background: '#FFFFFF', border: '1px solid #E5E5EA', borderRadius: 12, display: 'flex', alignItems: 'center', padding: '0 14px', height: 40, boxShadow: '0 1px 2px rgba(28,28,30,0.04)' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8E8E93" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
          </svg>
          <input
            type="text"
            placeholder="Buscar por medicamento, lote, laboratório ou datas"
            value={stockQuery}
            onChange={(event) => setStockQuery(event.target.value)}
            style={{ flex: 1, border: 'none', outline: 'none', padding: '0 10px', fontSize: 13, color: '#1C1C1E', background: 'transparent', fontFamily: 'inherit', letterSpacing: '-0.005em' }}
          />
        </div>
      </div>

      {/* Tabela */}
      <div style={{ background: '#FFFFFF', border: '1px solid #E5E5EA', borderRadius: 22, overflow: 'hidden', boxShadow: '0 1px 2px rgba(28,28,30,0.04), 0 12px 32px -22px rgba(28,28,30,0.12)' }}>
        {/* Header */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.1fr 1fr 90px 1.1fr 1.2fr 110px', gap: 16, padding: '14px 22px', background: '#FAFAFB', borderBottom: '1px solid #E5E5EA', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#6E6E73', alignItems: 'center' }}>
          {[
            { key: 'name',          label: 'Medicamento' },
            { key: 'laboratory',    label: 'Laboratório' },
            { key: 'lastEntryDate', label: 'Última entrada' },
            { key: 'lastLot',       label: 'Lote' },
            { key: 'lastExpiry',    label: 'Validade' },
            { key: 'totalQuantity', label: 'Quantidade' },
          ].map(({ key, label }) => (
            <div key={key} onClick={() => handleSort(key)} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
              {label}
              <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
                <path d={sortConfig.field === key && sortConfig.direction === 'asc' ? 'M5 8.5v-7M5 1.5l2.5 2.5M5 1.5L2.5 4' : 'M5 1.5v7M5 8.5l2.5-2.5M5 8.5L2.5 6'} stroke={sortConfig.field === key ? '#1C1C1E' : '#A8A8AE'} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          ))}
          <div>Status</div>
        </div>

        {/* Rows */}
        {loadingInventory ? (
          <div style={{ padding: '48px 22px', textAlign: 'center', color: '#8E8E93', fontSize: 14 }}>Carregando estoque...</div>
        ) : sortedStockItems.length === 0 ? (
          <div style={{ padding: '48px 22px', textAlign: 'center', color: '#8E8E93', fontSize: 14 }}>Nenhuma movimentação encontrada com os filtros atuais.</div>
        ) : (
          sortedStockItems.map((item, index) => {
            const status = getStockStatus(item);
            const statusChips = {
              ok:      { bg: '#E6F1EB', fg: '#3D8068', dot: '#3D8068' },
              warning: { bg: '#FBEEDF', fg: '#A66730', dot: '#D4923A' },
              danger:  { bg: '#FBE0E5', fg: '#9F4A60', dot: '#9F4A60' },
            };
            const stat = statusChips[status.tone] || statusChips.ok;

            const days = daysUntilExpiry(item.lastExpiry);
            const validadeColor = days == null ? '#C7C7CC' : days < 0 ? '#9F4A60' : days <= 60 ? '#A66730' : '#1C1C1E';
            const validadeLabel = days == null ? null : days < 0 ? `vencido há ${Math.abs(days)} d` : days <= 60 ? `vence em ${days} d` : null;

            const pctOfMin = item.min > 0 ? Math.min(100, (Number(item.totalQuantity || 0) / item.min) * 100) : 100;
            const barColor = pctOfMin >= 100 ? '#3D8068' : pctOfMin >= 30 ? '#D4923A' : '#9F4A60';

            return (
              <div key={item._id} style={{ display: 'grid', gridTemplateColumns: '2fr 1.1fr 1fr 90px 1.1fr 1.2fr 110px', gap: 16, padding: '18px 22px', borderTop: index === 0 ? 'none' : '1px solid #F2F2F7', alignItems: 'center' }}>
                {/* Medicamento */}
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, cursor: 'pointer' }}
                  role="button" tabIndex={0}
                  onClick={() => handleNavigateToMedicationDetails(item)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleNavigateToMedicationDetails(item); } }}
                >
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(160deg,#EFE3F0 0%,#DECCDE 100%)', color: '#7A4A86', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4.5 12.5a6 6 0 018.5-8.5l7 7a6 6 0 01-8.5 8.5zM9 9l6 6" />
                    </svg>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#1C1C1E', letterSpacing: '-0.008em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                    <div style={{ fontSize: 11.5, color: '#8E8E93', marginTop: 2, letterSpacing: '-0.005em' }}>{item.category || '—'}</div>
                  </div>
                </div>

                {/* Laboratório */}
                <div style={{ fontSize: 13.5, fontWeight: 500, color: item.laboratory ? '#1C1C1E' : '#C7C7CC', letterSpacing: '-0.005em', whiteSpace: 'nowrap' }}>
                  {item.laboratory || '—'}
                </div>

                {/* Última entrada */}
                <div style={{ fontSize: 13.5, fontWeight: 500, color: item.lastEntryDate ? '#1C1C1E' : '#C7C7CC', letterSpacing: '-0.005em', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                  {item.lastEntryDate ? formatDate(item.lastEntryDate) : '—'}
                </div>

                {/* Lote */}
                <div>
                  {item.lastLot ? (
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#6E6E73', background: '#F2F2F7', padding: '3px 9px', borderRadius: 6, fontFamily: 'ui-monospace, monospace', letterSpacing: '-0.005em' }}>
                      {item.lastLot}
                    </span>
                  ) : (
                    <span style={{ fontSize: 13.5, color: '#C7C7CC' }}>—</span>
                  )}
                </div>

                {/* Validade */}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: validadeColor, letterSpacing: '-0.005em', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {item.lastExpiry ? formatDate(item.lastExpiry) : '—'}
                  </div>
                  {validadeLabel && (
                    <div style={{ fontSize: 10.5, color: validadeColor, marginTop: 2, letterSpacing: '-0.005em', fontWeight: 500 }}>{validadeLabel}</div>
                  )}
                </div>

                {/* Quantidade */}
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 5 }}>
                    <span style={{ fontSize: 16, fontWeight: 700, color: '#1C1C1E', letterSpacing: '-0.028em', fontVariantNumeric: 'tabular-nums' }}>
                      {Number(item.totalQuantity || 0).toLocaleString('pt-BR')}
                    </span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#8E8E93', letterSpacing: '-0.005em' }}>
                      {item.unit}{item.min > 0 ? ` / mín. ${item.min}` : ''}
                    </span>
                  </div>
                  {item.min > 0 && (
                    <div style={{ height: 4, background: '#F2F2F7', borderRadius: 999, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pctOfMin}%`, background: barColor, borderRadius: 999 }} />
                    </div>
                  )}
                </div>

                {/* Status */}
                <div>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 600, letterSpacing: '-0.005em', color: stat.fg, background: stat.bg, padding: '4px 10px', borderRadius: 999, whiteSpace: 'nowrap' }}>
                    <span style={{ width: 5, height: 5, borderRadius: 999, background: stat.dot }} />
                    {status.label}
                  </div>
                </div>
              </div>
            );
          })
        )}

        {/* Footer */}
        <div style={{ padding: '14px 22px', borderTop: '1px solid #F2F2F7', background: '#FAFAFB', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: '#6E6E73', letterSpacing: '-0.005em' }}>
          <span>{sortedStockItems.length} medicamento{sortedStockItems.length !== 1 ? 's' : ''}{stats.lowStock > 0 ? ` · ${stats.lowStock} em estado crítico` : ''}</span>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content inventory-modal" onClick={(event) => event.stopPropagation()}>
            <div className="inventory-modal-body">
              <div className="modal-header">
                <h2>{modalTitle}</h2>
                {modalType === 'entry' && (
                  <button type="button" className="ghost-button new-medication-toggle" onClick={() => handleOpenMedicationModal()}>
                    Cadastrar medicamento
                  </button>
                )}
              </div>
              <form className="inventory-form" onSubmit={handleSubmit}>
                <label className="inventory-medication-field">
                  <span>Medicamento</span>
                  <div className="inventory-autocomplete">
                    <input
                      type="text"
                      name="name"
                      className="mac-input"
                      placeholder="Ex: Dipirona Sódica"
                      value={formData.name}
                      onChange={handleChange}
                      onFocus={handleMedicationFocus}
                      onBlur={handleMedicationBlur}
                      autoComplete="off"
                      required
                    />
                    {shouldShowSuggestions && (
                      <div className="inventory-suggestions">
                        {medicationSuggestions.map((medication) => (
                          <button
                            type="button"
                            key={medication._id}
                            className="inventory-suggestion"
                            onMouseDown={(event) => {
                              event.preventDefault();
                              handleSelectMedication(medication);
                            }}
                          >
                            <span className="inventory-suggestion-title">{medication.name}</span>
                            {(medication.category || medication.laboratory) && (
                              <small>{medication.category || medication.laboratory}</small>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </label>

                <div className="form-grid inventory-grid">
                  <label>
                    Laboratório
                    <input
                      type="text"
                      name="laboratory"
                      className="mac-input"
                      placeholder="Ex: EMS"
                      value={formData.laboratory}
                      onChange={handleChange}
                    />
                  </label>
                  <label>
                    Categoria
                    <input
                      type="text"
                      name="category"
                      className="mac-input"
                      placeholder="Analgésico, antibiótico..."
                      value={formData.category}
                      onChange={handleChange}
                      required
                    />
                  </label>
                </div>

                <div className="form-grid inventory-grid">
                  <label>
                    Unidade
                    <input
                      type="text"
                      name="unit"
                      className="mac-input"
                      placeholder="caixas, frascos..."
                      value={formData.unit}
                      onChange={handleChange}
                      required
                    />
                  </label>
                  <label>
                    Estoque mínimo
                    <input
                      type="number"
                      min="0"
                      name="min"
                      className="mac-input"
                      placeholder="Quantia mínima desejada"
                      value={formData.min}
                      onChange={handleChange}
                      required
                    />
                  </label>
                </div>


                <div className="modal-actions">
                  <button type="button" className="ghost-button" onClick={handleCloseModal}>
                    Fechar
                  </button>
                  <button type="submit" className="mac-button">
                    {submitLabel}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
